import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SuiToastService } from '@stride-ui/components';
import { ApiErrorCode } from './api-error-code';
import { isRecord, toApiError } from './to-api-error';

export const API_ERROR_FALLBACK_KEY = 'api_errors.fallback';
export const API_ERROR_TOAST_TITLE_KEY = 'toasts.error_title';

export interface ApiErrorMessageOptions {
  overrides?: Partial<Record<ApiErrorCode, string>>;
  fallbackKey?: string;
  /**
   * Extra translation parameters, e.g. for a fallback text that needs a name.
   * The error's own `data` wins when both define the same parameter.
   */
  params?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class ApiErrorMessageService {
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(SuiToastService);

  messageFor(error: unknown, options: ApiErrorMessageOptions = {}): string {
    const fallbackKey = options.fallbackKey ?? API_ERROR_FALLBACK_KEY;
    const apiError = toApiError(error);
    if (!apiError) {
      return this.translate.instant(fallbackKey, options.params);
    }

    const data = isRecord(apiError.data)
      ? joinStringLists(apiError.data)
      : undefined;
    const params = options.params ? { ...options.params, ...data } : data;
    const key =
      options.overrides?.[apiError.code as ApiErrorCode] ??
      `api_errors.${apiError.code}`;
    const message: string = this.translate.instant(key, params);

    return message === key
      ? this.translate.instant(fallbackKey, params)
      : message;
  }

  toast(error: unknown, options?: ApiErrorMessageOptions): void {
    this.toastService.showErrorToast(
      this.translate.instant(API_ERROR_TOAST_TITLE_KEY),
      this.messageFor(error, options),
    );
  }
}

// MessageFormat renders an array placeholder as "a,b"; a text such as the
// discount names of 17002 needs "a, b".
function joinStringLists(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      isStringList(value) ? value.join(', ') : value,
    ]),
  );
}

function isStringList(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
}
